import { DateValue } from '@internationalized/date';
import type { RangeValue } from '@react-types/shared';

export const ReportType = {
    Activity: 0,
    StoppageEvents: 1,
    PortCalls: 2,
    Voyages: 3,
    DailyAisSignals: 4,
} as const;

export type ReportType = typeof ReportType[keyof typeof ReportType];

export interface IDateRangeValue {
    startDate: Date | null;
    endDate: Date | null;
}

export interface ILocationFilter {
    type: 'all' | 'latlong' | 'seaArea' | 'polygon' | 'port';
    coordinates?: {
        lat?: number | null;
        lon?: number | null;
        latTo?: number | null;
        lonTo?: number | null;
    };
    areaId?: string[];
    polygonId?: string[];
    portNames?: string[];
    portPolygonIds?: string[];
}

export interface IRangeFilter {
    min?: number | null | undefined;
    max?: number | null | undefined;
}

export interface ISelectFilter {
    value: string;
    label: string;
}

export interface IAdditionalFilter {
    imoNumbers: number[];
    sectors: string[];
    dateRange?: RangeValue<DateValue>;
    location?: ILocationFilter;
    originDateRangeAndLocation?: {
        dateRange?: RangeValue<DateValue>;
        location?: ILocationFilter;
    };
    destinationDateRangeAndLocation?: {
        dateRange?: RangeValue<DateValue>;
        location?: ILocationFilter;
    };
    signalSource?: ISelectFilter | null;
    heading?: IRangeFilter;
    ballastLaden?: ISelectFilter | null;
    designDraft?: IRangeFilter;
    speed?: ISelectFilter | null;
    activity?: ISelectFilter | null;
    duration?: IRangeFilter;
}

export interface IDataField {
    id: string;
    label: string;
    category?: string;
    selected: boolean;
    columnSizeCsv: number;
    order: number;
    groupOrder?: number;
}

// Frequency types and constants
export type TFrequencyType = 'hourly' | 'daily' | 'monthly' | 'latest';

export const FREQUENCY_OPTIONS: { key: TFrequencyType; label: string }[] = [
    { key: 'hourly', label: 'Hourly' },
    { key: 'daily', label: 'Daily' },
    { key: 'monthly', label: 'Monthly' },
    { key: 'latest', label: 'Latest' },
];

export const BALLAST_LADEN_OPTIONS: { key: string; label: string }[] = [
    { key: 'Ballast', label: 'Ballast' },
    { key: 'Laden', label: 'Laden' },
];

export const PORT_CALLS_ACTIVITY_OPTIONS: { key: string; label: string }[] = [
    { key: 'Discharge', label: 'Discharge' },
    { key: 'Load', label: 'Load' },
    { key: 'In Shipyard', label: 'In Shipyard' },
    { key: 'In Shipbreaker', label: 'In Shipbreaker' },
    { key: '', label: 'Unclassified' },
];

export const STOPPAGE_EVENTS_ACTIVITY_OPTIONS: { key: string; label: string }[] = [
    { key: 'Stopped (at anchor)', label: 'Stopped (at anchor)' },
    { key: 'Stopped (moored)', label: 'Stopped (moored)' },
    { key: 'Stopped (Unclassified)', label: 'Stopped (Unclassified)' },
];

export interface IDataOption {
    frequency?: TFrequencyType;
}

export interface IFileInfo {
    recordCount: number;
    vesselCount: number;
    firstRecord: Date | string | null;
    lastRecord: Date | string | null;
    estimatedSize: string;
    fileType: string;
}

export interface IEstimatedFileInfo {
    estimatedSize: string;
    estimatedRecords: number;
}

// Report state interfaces that can be extended for different report types
export interface IBaseReportState {
    additionalFilters: IAdditionalFilter;
    dataFields: IDataField[];
    fileInfo: IFileInfo;
}

// Activity Report specific state (extends base state)
export interface IActivityReportState extends IBaseReportState {
    dataOptions: IDataOption;
    // Add activity-specific properties here if needed in the future
}

export interface IStoppageEventsState extends IBaseReportState {
    // Add stoppage events-specific properties here if needed in the future
}

export interface IPortCallsState extends IBaseReportState {
    // Add port calls-specific properties here if needed in the future
}

export interface IVoyagesState extends IBaseReportState {
    // Add voyages-specific properties here if needed in the future
}

export interface IDailyAisSignalsState extends IBaseReportState {
    dataOptions: IDataOption;
    // Add daily AIS signals-specific properties here if needed in the future
}